import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Typography,
  CircularProgress
} from '@mui/material';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import LinearScaleIcon from '@mui/icons-material/LinearScale';
import FolderIcon from '@mui/icons-material/Folder';
import DescriptionIcon from '@mui/icons-material/Description';
import WidgetsIcon from '@mui/icons-material/Widgets';
import { AppTooltip } from './Common';

const WorkflowActionNode = ({
  setting,
  index,
  totalSettings,
  isEnabled,
  isSelected,
  status,
  togglingScope,
  onToggleEnable,
  onToggleScope,
  onClick,
  onDelete,
  onMove,
  onMoveToFlow,
  onUngroup,
  onToggleGroupMode,
  onNameSave,
  onCopyAction,
  canDelete = true,
  workflowFlows = [],
  currentFlowId = null,
  isInParallelGroup = false,
  groupMode = 'sequential',
  isSearchActive = false,
  isSearchMatch = false,
  styles
}) => {
  const { t } = useTranslation();
  const [editingName, setEditingName] = useState(false);
  const [editingNameValue, setEditingNameValue] = useState('');
  const [originalEditingName, setOriginalEditingName] = useState('');
  const [moveMenuAnchorEl, setMoveMenuAnchorEl] = useState(null);
  const [isHovered, setIsHovered] = useState(false);

  const isDisabled = !isEnabled;
  const isWidgetUpdate = setting.type === 'generatewidget';
  const name = (setting.customData?.tag) ? `${setting.name}\n(${setting.customData.tag})` : setting.name;
  const iconSrc = setting.iconSrc;
  const isTemporary = typeof setting.id === 'string' && setting.id.startsWith('temp_');
  const moveTargetFlows = Array.isArray(workflowFlows)
    ? workflowFlows.filter((flow) => String(flow?.id) !== String(currentFlowId))
    : [];

  const handleNameClick = (currentName) => {
    if (isTemporary) return;
    setEditingName(true);
    setEditingNameValue(currentName);
    setOriginalEditingName(currentName);
  };

  const handleNameChange = (event) => {
    setEditingNameValue(event.target.value);
  };

  const handleNameSaveClick = () => {
    const trimmedName = editingNameValue.trim();
    if (!trimmedName || trimmedName === originalEditingName) {
      setEditingName(false);
      setEditingNameValue('');
      setOriginalEditingName('');
      return;
    }
    onNameSave(setting.id, trimmedName);
    setEditingName(false);
    setEditingNameValue('');
    setOriginalEditingName('');
  };

  const handleMoveMenuOpen = (event) => {
    event.stopPropagation();
    setMoveMenuAnchorEl(event.currentTarget);
  };

  const handleMoveMenuClose = () => {
    setMoveMenuAnchorEl(null);
  };

  const handleMoveToFlowSelect = (flowId) => {
    handleMoveMenuClose();
    if (onMoveToFlow) {
      onMoveToFlow(setting.id, flowId);
    }
  };

  return (
    <Box
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        mb: 3,
        position: 'relative',
        minWidth: { xs: '100px', md: '140px' },
        pt: '25px',
        opacity: isSearchActive && !isSearchMatch ? 0.38 : 1,
        transform: isSearchActive && isSearchMatch ? 'translateY(-2px)' : 'none',
        transition: 'opacity 180ms ease, transform 180ms ease',
      }}
    >
      {/* Controls Group */}
      <Box sx={{
        position: 'absolute',
        top: { xs: 0, md: 8 },
        left: '50%',
        display: 'flex',
        alignItems: 'center',
        gap: 0.25,
        zIndex: 2,
        background: isDisabled
          ? 'linear-gradient(135deg, rgba(248, 248, 248, 0.8) 0%, rgba(240, 240, 240, 0.8) 100%)'
          : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 249, 250, 0.95) 100%)',
        borderRadius: '24px',
        padding: '6px 8px',
        boxShadow: isDisabled
          ? '0 2px 8px rgba(87, 87, 176, 0.1), inset 0 1px 2px rgba(255, 255, 255, 0.3)'
          : '0 4px 16px rgba(87, 87, 176, 0.12), 0 2px 8px rgba(87, 87, 176, 0.08)',
        border: isDisabled
          ? '1px solid rgba(87, 87, 176, 0.2)'
          : '1px solid rgba(87, 87, 176, 0.15)',
        backdropFilter: 'blur(8px)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        opacity: (isHovered || Boolean(moveMenuAnchorEl)) ? 1 : 0,
        pointerEvents: (isHovered || Boolean(moveMenuAnchorEl)) ? 'auto' : 'none',
        transform: (isHovered || Boolean(moveMenuAnchorEl))
          ? 'translateX(-50%) translateY(0px) scale(1)'
          : 'translateX(-50%) translateY(-6px) scale(0.98)',
        '@media (hover: none), (pointer: coarse)': {
          opacity: 1,
          pointerEvents: 'auto',
          transform: 'translateX(-50%) translateY(0px) scale(1)',
        },
        '&:hover': {
          backgroundColor: isDisabled
            ? 'rgba(248, 248, 248, 0.9)'
            : 'rgba(240, 240, 255, 0.98)',
          boxShadow: isDisabled
            ? '0 3px 12px rgba(87, 87, 176, 0.12), inset 0 1px 2px rgba(255, 255, 255, 0.3)'
            : '0 8px 24px rgba(87, 87, 176, 0.25), 0 4px 8px rgba(87, 87, 176, 0.15)',
          transform: 'translateX(-50%) translateY(-2px) scale(1.05)',
          opacity: isDisabled ? 0.8 : 1,
        }
      }}>
        {totalSettings > 1 && index > 0 && (
          <AppTooltip title={t('workflow.move_left', 'Move Left')}>
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); onMove('left'); }}
              sx={styles.controlButton}
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          </AppTooltip>
        )}
        {canDelete && (
          <AppTooltip title={t('settings.delete_workflow_item', 'Delete this item')}>
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); onDelete(); }}
              sx={styles.deleteButton}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </AppTooltip>
        )}
        {onCopyAction && !isTemporary && (
          <AppTooltip title={t('copy_workflow_item', 'Duplicate this item')}>
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); onCopyAction(setting); }}
              sx={styles.controlButton}
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </AppTooltip>
        )}
        {moveTargetFlows.length > 0 && (
          <AppTooltip title={t('workflow.move_to_flow', 'Move to Flow')}>
            <IconButton
              size="small"
              onClick={handleMoveMenuOpen}
              aria-label={t('workflow.move_to_flow', 'Move to Flow')}
              aria-haspopup="menu"
              aria-expanded={Boolean(moveMenuAnchorEl)}
              sx={styles.controlButton}
            >
              <DriveFileMoveIcon fontSize="small" />
            </IconButton>
          </AppTooltip>
        )}
        {isInParallelGroup && (
          <AppTooltip title={t('workflow.ungroup_action', 'Remove from parallel group')}>
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); onUngroup?.(setting.id); }}
              sx={styles.controlButton}
            >
              <LinkOffIcon fontSize="small" />
            </IconButton>
          </AppTooltip>
        )}
        {isInParallelGroup && (
          <AppTooltip title={groupMode === 'parallel' ? t('workflow.switch_group_sequential', 'Run group sequentially') : t('workflow.switch_group_parallel', 'Run group in parallel')}>
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); onToggleGroupMode?.(setting.execution_group, groupMode === 'parallel' ? 'sequential' : 'parallel'); }}
              sx={styles.controlButton}
            >
              <LinearScaleIcon fontSize="small" />
            </IconButton>
          </AppTooltip>
        )}
        {totalSettings > 1 && index < totalSettings - 1 && (
          <AppTooltip title={t('workflow.move_right', 'Move Right')}>
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); onMove('right'); }}
              sx={styles.controlButton}
            >
              <ArrowForwardIcon fontSize="small" />
            </IconButton>
          </AppTooltip>
        )}
      </Box>
      <Menu
        anchorEl={moveMenuAnchorEl}
        open={Boolean(moveMenuAnchorEl)}
        onClose={handleMoveMenuClose}
        onClick={(event) => event.stopPropagation()}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center'
        }}
        PaperProps={{
          sx: {
            mt: 1,
            borderRadius: '12px',
            minWidth: 220
          }
        }}
      >
        {moveTargetFlows.map((flow) => (
          <MenuItem
            key={flow.id}
            onClick={() => handleMoveToFlowSelect(flow.id)}
          >
            {String(flow?.name || `Flow ${flow.id}`)}
          </MenuItem>
        ))}
      </Menu>

      {/* Connecting line */}
      <Box sx={{
        position: 'absolute',
        top: 32,
        left: '50%',
        transform: 'translateX(-50%)',
        width: '2px',
        height: '8px',
        background: 'linear-gradient(to bottom, rgba(0, 0, 0, 0.1), transparent)',
        borderRadius: '1px',
        zIndex: 1,
      }} />

      {/* Main Button + Satellites Wrapper */}
      <Box sx={{
        position: 'relative',
        width: { xs: '60px', md: '80px' },
        height: { xs: '60px', md: '80px' },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        {/* Main Action Button */}
        <AppTooltip
          title={
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {name}
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.6 }}>
                Click to configure this action
              </Typography>
            </Box>
          }
          placement="top"
          arrow
        >
          <Box
            onClick={onClick}
            sx={(() => {
              let backgroundColor, borderColor, boxShadow, background;

              if (status === 'started') {
                backgroundColor = '#2196f3';
                borderColor = '#1976d2';
                background = 'linear-gradient(135deg, #42a5f5 0%, #2196f3 50%, #1976d2 100%)';
                boxShadow = '0 8px 32px rgba(33, 150, 243, 0.5), 0 4px 16px rgba(33, 150, 243, 0.3)';
              } else if (status === 'finished') {
                backgroundColor = '#4caf50';
                borderColor = '#388e3c';
                background = 'linear-gradient(135deg, #66bb6a 0%, #4caf50 50%, #388e3c 100%)';
                boxShadow = '0 6px 24px rgba(76, 175, 80, 0.4), 0 3px 12px rgba(76, 175, 80, 0.2)';
              } else if (isWidgetUpdate) {
                backgroundColor = isDisabled ? '#eef7f7' : '#ecfbfb';
                borderColor = isDisabled ? 'rgba(0, 137, 148, 0.36)' : isSelected ? '#007c89' : '#00a6a6';
                background = isDisabled
                  ? 'linear-gradient(135deg, #eef7f7 0%, #e5eeee 100%)'
                  : 'linear-gradient(135deg, #f2ffff 0%, #dff7f7 52%, #c7eded 100%)';
                boxShadow = isDisabled
                  ? '0 2px 10px rgba(0, 137, 148, 0.12), inset 0 1px 2px rgba(255, 255, 255, 0.5)'
                  : isSelected
                    ? '0 10px 28px rgba(0, 124, 137, 0.32), 0 0 0 4px rgba(0, 166, 166, 0.18)'
                    : '0 7px 22px rgba(0, 137, 148, 0.24), inset 0 1px 2px rgba(255, 255, 255, 0.65)';
              } else if (status === 'pending') {
                backgroundColor = '#f2f1ff';
                borderColor = '#7777cf';
                background = 'linear-gradient(135deg, #faf9ff 0%, #ebe9ff 52%, #dad7f7 100%)';
                boxShadow = '0 7px 22px rgba(87, 87, 176, 0.22), inset 0 1px 2px rgba(255, 255, 255, 0.7)';
              } else if (isSelected) {
                backgroundColor = '#efedff';
                borderColor = '#5757b0';
                background = 'linear-gradient(135deg, #faf9ff 0%, #e7e4ff 52%, #d2cef5 100%)';
                boxShadow = '0 10px 28px rgba(87, 87, 176, 0.32), 0 0 0 4px rgba(119, 119, 207, 0.18)';
              } else if (isDisabled) {
                backgroundColor = '#f1f0f7';
                borderColor = 'rgba(87, 87, 176, 0.34)';
                background = 'linear-gradient(135deg, #f5f4fa 0%, #e7e5ef 100%)';
                boxShadow = '0 2px 10px rgba(87, 87, 176, 0.12), inset 0 1px 2px rgba(255, 255, 255, 0.5)';
              } else {
                backgroundColor = '#f2f1ff';
                borderColor = '#7777cf';
                background = 'linear-gradient(135deg, #faf9ff 0%, #ebe9ff 52%, #dad7f7 100%)';
                boxShadow = '0 7px 22px rgba(87, 87, 176, 0.22), inset 0 1px 2px rgba(255, 255, 255, 0.7)';
              }

              return {
                backgroundColor,
                color: (status === 'started' || status === 'finished') ? '#ffffff' : '#37376f',
                border: isSearchActive && isSearchMatch ? '3px solid #f2c94c' : `3px solid ${borderColor}`,
                background: isSearchActive && isSearchMatch
                  ? 'linear-gradient(135deg, #fffdf2 0%, #fff7c2 100%)'
                  : background,
                borderRadius: isWidgetUpdate ? '18px' : '50%',
                padding: '0px',
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                position: 'relative',
                boxShadow: isSearchActive && isSearchMatch
                  ? '0 0 0 5px rgba(242, 201, 76, 0.28), 0 10px 30px rgba(166, 107, 0, 0.24)'
                  : boxShadow,
                opacity: isDisabled ? 0.7 : 1,
                transform: isDisabled ? 'scale(0.95)' : 'scale(1)',
                filter: isDisabled ? 'saturate(0.3) brightness(0.9)' : 'none',
                outline: !isSearchActive ? '1px solid rgba(255, 255, 255, 0.8)' : 'none',
                outlineOffset: '-8px',
                ...(isSearchActive && isSearchMatch && {
                  animation: 'workflowSearchPulse 1.8s ease-in-out infinite',
                  '@keyframes workflowSearchPulse': {
                    '0%, 100%': {
                      boxShadow: '0 0 0 5px rgba(242, 201, 76, 0.24), 0 10px 30px rgba(166, 107, 0, 0.22)'
                    },
                    '50%': {
                      boxShadow: '0 0 0 8px rgba(242, 201, 76, 0.34), 0 14px 36px rgba(166, 107, 0, 0.28)'
                    }
                  }
                }),
                ...(status === 'started' && {
                  animation: 'processingPulse 1.5s ease-in-out infinite',
                  '@keyframes processingPulse': {
                    '0%': {
                      boxShadow: '0 8px 32px rgba(33, 150, 243, 0.5), 0 4px 16px rgba(33, 150, 243, 0.3)',
                      transform: 'scale(1)'
                    },
                    '50%': {
                      boxShadow: '0 12px 40px rgba(33, 150, 243, 0.7), 0 6px 20px rgba(33, 150, 243, 0.5)',
                      transform: 'scale(1.05)'
                    },
                    '100%': {
                      boxShadow: '0 8px 32px rgba(33, 150, 243, 0.5), 0 4px 16px rgba(33, 150, 243, 0.3)',
                      transform: 'scale(1)'
                    }
                  }
                }),
                '&:hover': {
                  transform: isDisabled ? 'scale(0.98) translateY(-1px)' : 'translateY(-3px) scale(1.02)',
                  boxShadow: isDisabled
                    ? '0 6px 16px rgba(0, 0, 0, 0.12), inset 0 1px 3px rgba(0, 0, 0, 0.08)'
                    : status === 'started'
                      ? '0 12px 40px rgba(33, 150, 243, 0.6), 0 6px 20px rgba(33, 150, 243, 0.4)'
                      : status === 'finished'
                        ? '0 8px 32px rgba(76, 175, 80, 0.5), 0 4px 16px rgba(76, 175, 80, 0.3)'
                        : isSelected
                          ? '0 12px 32px rgba(87, 87, 176, 0.4), 0 6px 12px rgba(87, 87, 176, 0.3)'
                          : '0 9px 24px rgba(87, 87, 176, 0.23), 0 4px 8px rgba(87, 87, 176, 0.13)',
                  opacity: isDisabled ? 0.85 : 1,
                  backgroundColor: status === 'started' || status === 'finished' || isSelected ? undefined : '#f0f0ff',
                },
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: '-10px',
                  left: '50%',
                  transform: 'translateX(-50%) rotate(45deg)',
                  width: '10px',
                  height: '10px',
                  border: '1px solid #dddddd',
                  borderWidth: '0 1px 1px 0',
                  backgroundColor: status === 'started'
                    ? '#2196f3'
                    : status === 'finished'
                      ? '#4caf50'
                      : isWidgetUpdate
                        ? '#dff7f7'
                        : isSelected
                          ? '#e7e4ff'
                          : '#ebe9ff',
                  display: 'block'
                }
              };
            })()}
          >
            {isWidgetUpdate && (
              <Box
                sx={{
                  position: 'absolute',
                  top: -12,
                  right: -12,
                  width: 34,
                  height: 24,
                  borderRadius: '999px',
                  backgroundColor: isDisabled ? '#d7e4e4' : '#007c89',
                  color: '#ffffff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 5px 14px rgba(0, 124, 137, 0.28)',
                  border: '2px solid #ffffff',
                  zIndex: 6
                }}
              >
                <WidgetsIcon sx={{ fontSize: 16 }} />
              </Box>
            )}
            <Box
              component="img"
              src={iconSrc}
              alt={setting.type}
              draggable={false}
              onDragStart={(event) => event.preventDefault()}
              sx={{
              width: { xs: 35, md: 50 },
              height: { xs: 35, md: 50 },
              filter: isDisabled
                ? 'grayscale(1) brightness(0.6) contrast(0.8)'
                : isSelected
                  ? 'brightness(1.2) contrast(1.3) saturate(1.2) drop-shadow(0 3px 8px rgba(255, 255, 255, 0.8))'
                  : 'contrast(1.2) brightness(1.1) saturate(1.1) drop-shadow(0 2px 6px rgba(0, 0, 0, 0.2))',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              opacity: isDisabled ? 0.6 : 1,
              transform: isDisabled ? 'scale(0.9)' : 'scale(1)',
              zIndex: 2,
              position: 'relative',
              userSelect: 'none',
              WebkitUserDrag: 'none',
              pointerEvents: 'none',
            }} />

            {/* Processing/Completed Overlay */}
            {(status === 'started' || status === 'finished') && (
              <Box sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                height: '100%',
                backgroundColor: 'rgba(0, 0, 0, 0.1)',
                borderRadius: '12px',
                backdropFilter: 'blur(2px)',
              }}>
                {status === 'started' ? (
                  <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    color: 'white'
                  }}>
                    <CircularProgress
                      size={32}
                      sx={{
                        color: 'white',
                        mb: 0.5,
                        filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))'
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        textShadow: '0 1px 2px rgba(0,0,0,0.5)',
                        fontSize: '0.7rem'
                      }}
                    >
                      Processing...
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{
                    width: 40,
                    height: 40,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                  }}>
                    <Typography
                      sx={{
                        fontSize: '1.5rem',
                        color: '#4caf50',
                        fontWeight: 'bold'
                      }}
                    >
                      ✓
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            {/* Disabled overlay */}
            {isDisabled && (
              <Box sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.2) 0%, rgba(238, 238, 238, 0.3) 100%)',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'none',
                zIndex: 3,
                '&::before': {
                  content: '"⏸"',
                  fontSize: '20px',
                  color: 'rgba(158, 158, 158, 0.8)',
                  textShadow: '0 1px 2px rgba(255, 255, 255, 0.8)',
                  animation: 'float 3s ease-in-out infinite',
                  '@keyframes float': {
                    '0%, 100%': { transform: 'translateY(0px)' },
                    '50%': { transform: 'translateY(-2px)' }
                  }
                }
              }} />
            )}
          </Box>
        </AppTooltip>

        {/* Status Badge - Toggle (Satellite Bottom) */}
        <Box sx={{
          position: 'absolute',
          bottom: { xs: '-15px', md: '-20px' },
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 10,
        }}>
          <AppTooltip title={isEnabled ? t('workflow.status.running_click_to_stop', 'Running - Click to Stop') : t('workflow.status.stopped_click_to_start', 'Stopped - Click to Start')}>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                onToggleEnable();
              }}
              sx={{
                color: isEnabled ? 'rgba(90, 182, 95, 1)' : 'rgba(0,0,0,1)',
                p: 0,
                width: 32,
                height: 32,
                transition: 'color 150ms ease, transform 120ms ease',
                backgroundColor: 'transparent',
                boxShadow: 'none',
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                '&:hover': {
                  color: isEnabled ? 'rgba(46, 125, 50, 0.85)' : 'rgba(0,0,0,0.65)',
                  transform: 'translateY(-3px)',
                  backgroundColor: 'transparent'
                },
                '&.Mui-disabled': {
                  color: 'rgba(0,0,0,0.18)'
                }
              }}
              size="medium"
            >
              {isEnabled ? <ToggleOnIcon sx={{ fontSize: 26 }} /> : <ToggleOffIcon sx={{ fontSize: 26 }} />}
            </IconButton>
          </AppTooltip>
        </Box>

        {/* Scope Indicator (Satellite Right) */}
        <Box sx={{
          position: 'absolute',
          top: '50%',
          right: { xs: '-8px', md: '-10px' },
          transform: 'translateY(-50%)',
          zIndex: 10,
        }}>
          <AppTooltip
            title={
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'white' }}>
                  {togglingScope ? 'Updating...' : (setting.scope === 'project' ? 'Project Action' : 'Event Action')}
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'rgba(255,255,255,0.9)' }}>
                  {setting.scope === 'project'
                    ? 'Run when all events/files are processed'
                    : 'Runs for each individual event/file'
                  }
                </Typography>
                {setting.type !== 'generatewidget' && !togglingScope && (
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#90caf9', fontWeight: 600 }}>
                    Click to toggle scope
                  </Typography>
                )}
              </Box>
            }
            placement="bottom"
            arrow
          >
            <Box
              data-canvas-interactive="true"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                if (setting.type !== 'generatewidget' && !togglingScope) {
                  onToggleScope();
                }
              }}
              sx={{
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                backgroundColor: isWidgetUpdate
                  ? 'transparent'
                  : setting.scope === 'project' ? '#fdebed' : '#eceaff',
                color: setting.scope === 'project' ? '#a33b42' : '#5757b0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: isWidgetUpdate
                  ? 'none'
                  : `1px solid ${setting.scope === 'project' ? 'rgba(163, 59, 66, 0.34)' : 'rgba(87, 87, 176, 0.34)'}`,
                boxShadow: isWidgetUpdate
                  ? 'none'
                  : setting.scope === 'project'
                    ? '0 3px 9px rgba(163, 59, 66, 0.18)'
                    : '0 3px 9px rgba(87, 87, 176, 0.18)',
                cursor: (setting.type === 'generatewidget' || togglingScope) ? 'default' : 'pointer',
                transition: 'all 0.18s ease',
                opacity: (isWidgetUpdate || togglingScope) ? 0.75 : 1,
                pointerEvents: togglingScope ? 'none' : 'auto',
                '&:hover': {
                  transform: (isWidgetUpdate || togglingScope) ? 'none' : 'scale(1.12)',
                  boxShadow: isWidgetUpdate ? 'none' : undefined,
                  backgroundColor: (isWidgetUpdate || togglingScope)
                    ? (isWidgetUpdate ? 'transparent' : setting.scope === 'project' ? '#f8e2e4' : '#e5e2f7')
                    : (setting.scope === 'project' ? '#f9dadd' : '#dfdcff'),
                }
              }}>
              {togglingScope ? (
                <CircularProgress
                  size={12}
                  thickness={4}
                  sx={{
                    color: setting.scope === 'project' ? '#872e32ff' : '#796dd9',
                    animation: 'spin 1s linear infinite',
                    '@keyframes spin': {
                      '0%': { transform: 'rotate(0deg)' },
                      '100%': { transform: 'rotate(360deg)' }
                    }
                  }}
                />
              ) : setting.scope === 'project' ? (
                <FolderIcon sx={{ fontSize: '14px' }} />
              ) : (
                <DescriptionIcon sx={{ fontSize: '14px' }} />
              )}
            </Box>
          </AppTooltip>
        </Box>
      </Box>

      {/* Text Label Below Circular Button */}
      <Box sx={{
        mt: 1.5,
        textAlign: 'center',
        minHeight: '32px',
        maxHeight: '50px',
        maxWidth: { xs: '100px', md: '150px' },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden'
      }}>
        {editingName ? (
          <TextField
            data-canvas-interactive="true"
            value={editingNameValue}
            onChange={handleNameChange}
            onBlur={handleNameSaveClick}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleNameSaveClick();
                e.preventDefault();
              } else if (e.key === 'Escape') {
                setEditingName(false);
                setEditingNameValue('');
                setOriginalEditingName('');
                e.preventDefault();
              }
            }}
            autoFocus
            variant="standard"
            size="small"
            sx={{
              fontSize: { xs: '10px', md: '12px' },
              width: '95%',
              '& .MuiInputBase-input': {
                textAlign: 'center',
                fontWeight: isSelected ? 600 : 500,
                color: isWidgetUpdate ? '#004f59' : '#37376f',
                fontSize: { xs: '10px', md: '12px' },
              }
            }}
            inputProps={{
              style: {
                textAlign: 'center',
                fontWeight: isSelected ? 600 : 500,
                color: isWidgetUpdate ? '#004f59' : '#37376f',
              }
            }}
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <AppTooltip title={name} arrow placement="top">
            <Typography
              data-canvas-interactive="true"
              onClick={(e) => {
                if (!isTemporary) {
                  handleNameClick(setting.name || '');
                  e.stopPropagation();
                }
              }}
              sx={{
                fontSize: { xs: '10px', md: '12px' },
                fontWeight: isSelected ? 600 : 500,
                textAlign: 'center',
                width: '100%',
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                lineHeight: '1.2',
                cursor: isTemporary ? 'default' : 'pointer',
                padding: '4px 6px',
                borderRadius: '6px',
                opacity: setting.enable === 0 ? 0.8 : 1,
                background: isSearchActive && isSearchMatch
                  ? 'linear-gradient(135deg, #fff7d6 0%, #fff3bd 100%)'
                  : setting.enable === 0
                    ? 'linear-gradient(135deg, rgba(158, 158, 158, 0.1) 0%, rgba(189, 189, 189, 0.1) 100%)'
                    : isWidgetUpdate
                      ? 'linear-gradient(135deg, rgba(0, 166, 166, 0.12) 0%, rgba(0, 124, 137, 0.08) 100%)'
                      : 'linear-gradient(135deg, rgba(119, 119, 207, 0.14) 0%, rgba(87, 87, 176, 0.09) 100%)',
                border: isSearchActive && isSearchMatch
                  ? '1px solid #f2c94c'
                  : isWidgetUpdate
                    ? '1px solid rgba(0, 137, 148, 0.24)'
                    : setting.enable === 0
                      ? '1px solid rgba(158, 158, 158, 0.2)'
                      : '1px solid rgba(87, 87, 176, 0.24)',
                color: isSearchActive && isSearchMatch ? '#6b4f00' : isWidgetUpdate ? '#004f59' : '#37376f',
                position: 'relative',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': isTemporary ? {} : {
                  backgroundColor: setting.enable === 0
                    ? 'rgba(158, 158, 158, 0.15)'
                    : isWidgetUpdate
                      ? 'rgba(0, 166, 166, 0.16)'
                    : isSelected
                      ? 'rgba(255,255,255,0.15)'
                      : 'rgba(87, 87, 176, 0.08)',
                  transform: 'scale(1.02)',
                }
              }}
            >
              {name} {isTemporary ? ` (${t('settings.new_indicator')})` : ""}
            </Typography>
          </AppTooltip>
        )}

        {/* Status indicator */}
        {status !== 'idle' && (
          <Box sx={{
            mt: 0.5,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            <Typography
              variant="caption"
              sx={{
                fontSize: '0.6rem',
                fontWeight: 600,
                padding: '2px 6px',
                borderRadius: '8px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                backgroundColor: status === 'pending'
                  ? 'rgba(158, 158, 158, 0.8)'
                  : status === 'started'
                    ? 'rgba(33, 150, 243, 0.9)'
                    : 'rgba(76, 175, 80, 0.9)',
                color: 'white',
                textShadow: '0 1px 2px rgba(0,0,0,0.5)',
                minWidth: '50px',
                textAlign: 'center'
              }}
            >
              {status === 'pending' ? 'Pending' : status === 'started' ? 'Started' : 'Finished'}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default WorkflowActionNode;
